import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ROLE_EDIT, ROLE_ADD } from "../../../../services/ApiCalls";
import ButtonLoader from "../../../../components/Common/ButtonLoader";
import { useTranslation } from "react-i18next";

const RoleForm = ({ data }) => {
  const { t } = useTranslation();
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      _id: data?._id || "",
      roleName: data?.roleName || "",
      status: data?.status === "Active" || data?.status === true,
      permission: {},
    },
  });

  const defaultPermissions = [
    { module: "Dashboard" , label : "Dashboard"},
    {
      module: "ManageHotel",
      label : "Manage Hotel",
    },
    {
      module: "ManageBooking",
      label : "Manage Booking",
     },
    { module: "Finance" , label : "Finance" },
    { module: "ManageCoupon" , label : "Manage Coupon"},
    { module: "Review" , label : "Review" },
    { module: "Report" , label : "Report" },
    { module: "Notification" , label : "Notification" },
    { module: "SystemAccess" , label : "System Access" },
    { module: "Settings" , label : "Settings" },
  ];

  useEffect(() => {
    if (data?.permission) {
      const permissionObj = data.permission;
      Object.keys(permissionObj).forEach((modKey) => {
        const moduleData = permissionObj[modKey];
        if (moduleData.sections) {
          Object.keys(moduleData.sections).forEach((sectionKey) => {
            const perms = moduleData.sections[sectionKey];
            setValue(
              `permission.${modKey}.sections.${sectionKey}.view`,
              perms.view
            );
            setValue(
              `permission.${modKey}.sections.${sectionKey}.add_edit`,
              perms.add_edit
            );
            setValue(
              `permission.${modKey}.sections.${sectionKey}.delete`,
              perms.delete
            );
          });
        } else {
          setValue(`permission.${modKey}.view`, moduleData.view);
          setValue(`permission.${modKey}.add_edit`, moduleData.add_edit);
          setValue(`permission.${modKey}.delete`, moduleData.delete);
        }
      });
    }
  }, [data, setValue]);

  const onSubmit = async (formData) => {
    try {
      setLoader(true);
      const payload = {
        ...formData,
        status: formData.status ? "Active" : "Inactive",
      };

      let response;
      if (data?._id) {
        response = await ROLE_EDIT(payload);
      } else {
        delete payload._id;
        response = await ROLE_ADD(payload);
      }

      if (response?.data?.status === "success") {
        toast.success(response?.data?.message || t("systemAccess.submitSuccess"));
        navigate("/role");
      } else {
        toast.error(response?.data?.message);
      }
    } catch (error) {
      toast.error(error?.message || t("systemAccess.submitFailed"));
    } finally {
      setLoader(false);
    }
  };

  const renderToggle = (id, path) => (
    <div className="form-check form-switch permisionSwitch">
      <input type="checkbox" className="form-check-input" id={id} {...register(path)} />
    </div>
  );

  return (
    <Col lg="12" className="my-2">
      <div className="cardCstm p-3 rounded bg-white py-5">
        <Form className="mx-auto" style={{ maxWidth: 777 }} onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col md="6" className="my-2 my-lg-4">
              <label className="form-label text-muted m-0 pb-1 fw-sbold">
                {t("systemAccess.role")}
              </label>
              <input
                type="text"
                className="form-control"
                placeholder={t("systemAccess.enterRole")}
                {...register("roleName", {
                  required: t("systemAccess.roleRequired"),
                })}
              />
              {errors?.roleName && (
                <p className="text-danger m-0">{errors.roleName.message}</p>
              )}
            </Col>

            <Col lg="6" className="my-2 permisionSwitch">
              <Form.Group controlId="status">
                <Form.Label className="form-label text-muted m-0 pb-1 fw-sbold mt-3">
                  {t("systemAccess.status")}
                </Form.Label>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Form.Check
                      type="switch"
                      id="active-toggle"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />
              </Form.Group>
            </Col>

            <div className="card shadow-sm rounded-3 p-3 border-0 bg-light mt-4">
              <h5 className="fw-bold mb-3 d-flex align-items-center">
                {t("systemAccess.rolePermissions")}
                <span
                  className="ms-2"
                  data-bs-toggle="tooltip"
                  title={t("systemAccess.permissionHint")}
                >
                  <i className="bi bi-info-circle text-muted"></i>
                </span>
              </h5>

              <Table responsive bordered hover className="mb-0 text-center">
                <thead className="table-light">
                  <tr>
                    <th className="text-start">{t("systemAccess.moduleSection")}</th>
                    <th>{t("systemAccess.view")}</th>
                    <th>{t("systemAccess.addEdit")}</th>
                    <th>{t("systemAccess.delete")}</th>
                  </tr>
                </thead>
                <tbody>
                  {defaultPermissions.map((mod) => (
                    mod.sections ? (
                      mod.sections.map((section) => (
                        <tr key={`${mod.module}-${section}`}>
                          <td className="text-start ps-3">
                            {mod.module} - {section}
                          </td>
                          {["view", "add_edit", "delete"].map((perm) => (
                            <td key={perm}>
                              {renderToggle(
                                `${mod.module}-${section}-${perm}`,
                                `permission.${mod.module}.sections.${section}.${perm}`
                              )}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (<tr key={mod.module}>
                      <td className="text-start ps-3">{mod.module}</td>
                      {["view", "add_edit", "delete"].map((perm) => (
                        <td key={perm}>
                          {renderToggle(
                            `${mod.module}-${perm}`,
                            `permission.${mod.module}.${perm}`
                          )}
                        </td>
                      ))}
                    </tr>
                    )))}
                </tbody>
              </Table>
            </div>

            <Col lg="12" className="my-4 text-center">
              <Button type="submit" className="px-4 rounded-3" disabled={loader}>
                {loader ? <ButtonLoader /> : t("systemAccess.submit")}
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    </Col>
  );
};

export default RoleForm;
